import type {
  InventoryItem,
  InventoryItemFormData,
  StockMovement,
  InventoryLot,
  InventorySample,
  RoastedBulkGroup,
  RoastedBulkGroupItem,
} from '../../types/inventory';
import type { GreenBeanProfile, CreateGreenBeanProfileDTO } from '../../types/greenBean';
import type { RoastProfile, CuppingProfile } from '../profileService';

interface InventoryItemDto {
  id: string;
  code: string;
  name: string;
  description: string;
  unit_of_measure: string;
  item_type: 'green_bean' | 'roasted_bulk' | 'finished_good';
  reorder_point: number;
  reorder_quantity: number;
  standard_cost: number;
  status: string;
  quantity_on_hand?: number;
  quantity_reserved?: number;
  quantity_available?: number;
  expiry_date?: string;
  work_order_id?: string;
  wo_reference_id?: string;
  green_bean_profile_id?: string;
  location?: string;
  green_bean_profiles?: GreenBeanProfile[];
  roast_profiles?: RoastProfile[];
  cupping_profiles?: CuppingProfile[];
  average_price?: number;
  estimated_selling_price?: number;
  pack_size?: number;
  ingredients?: InventoryItemDto[];
  is_onboarding_sample?: boolean;
}

interface StockMovementDto {
  id: string;
  item_id: string;
  warehouse_id: string;
  movement_type: string;
  quantity: number;
  reference_type?: string;
  reference_id?: string;
  notes?: string;
  created_at: string;
  warehouse_name?: string;
  user_name?: string;
  unit_cost?: number;
  lot_id?: string;
  lot_number?: string;
}

interface CreateInventoryItemRequest {
  code?: string;
  name: string;
  description: string;
  unit_of_measure: string;
  item_type: string;
  reorder_point: number;
  reorder_quantity: number;
  standard_cost: number;
  estimated_selling_price?: number;
  expiry_date?: string;
  quantity?: number;
  location?: string;
  work_order_id?: string;
  status?: 'active' | 'inactive';
  green_bean_profile?: CreateGreenBeanProfileDTO;
  green_bean_profile_id?: string;
  new_green_bean_profile?: CreateGreenBeanProfileDTO;
  pack_size?: number;
}

interface InventoryLotDto {
  id: string;
  item_id: string;
  lot_number: string;
  initial_quantity: number;
  remaining_quantity: number;
  expiry_date?: string;
  received_date: string;
  supplier_id?: string;
  supplier_name?: string;
  supplier_email?: string;
  supplier_phone?: string;
  source_type: string;
  source_id?: string;
  source_ref?: string;
  item_name: string;
  item_code: string;
  unit_price?: number;
  is_sample?: boolean;
  photo_name?: string;
  photo_url?: string;
  photo_uploaded_at?: string;
}

interface InventorySampleDto {
  id: string;
  inventoryItemId: string;
  sampleNumber: string;
  receivedDate: string;
  notes?: string;
  qaPassed?: boolean | null;
  createdAt: string;
  greenBeanProfile?: { id: string };
  roastProfile?: { id: string };
  cuppingProfile?: { id: string };
  lotId?: string;
  lotNumber?: string;
}

interface RoastedBulkGroupDto {
  itemId: string;
  itemCode: string;
  itemName: string;
  roastType: string;
  totalStock: number;
  averagePrice: number;
  items?: RoastedBulkGroupItemDto[];
}

interface RoastedBulkGroupItemDto {
  workOrderId: string;
  workOrderNumber: string;
  inputWeight: number;
  outputWeight: number;
  shrinkageKg: number;
  shrinkagePercent: number;
  remainingQuantity: number;
  expiryDate?: string;
  status: string;
  inventoryItemId: string;
  lotId: string;
  unitPrice?: number;
  unitCostPerKg?: number;
  stockCreatedAt?: string;
}

export interface FinishedGoodsProductionRequest {
  final_product: {
    mode: 'existing' | 'new';
    item_id?: string;
    name?: string;
    location?: string;
    estimated_selling_price?: number;
    pack_size?: number;
  };
  final_quantity_pcs: number;
  components: {
    item_id: string;
    roast_type: string;
    quantity_kg: number;
  }[];
  misc_costs?: {
    name: string;
    unit_price: number;
    unit: number;
  }[];
}

export interface FinishedGoodsProductionResponse {
  final_item_id: string;
  final_quantity_pcs: number;
  production_run_id: string;
  component_consumptions: {
    item_id: string;
    total_quantity_kg: number;
    lot_breakdown: {
      lot_id: string;
      quantity_kg: number;
    }[];
  }[];
}

export type FinishedGoodsProductionStatus = 'draft' | 'cancelled' | 'completed';

export interface FinishedGoodsProductionListItem {
  id: string;
  code?: string;
  fg_id: string;
  fg_name: string;
  fg_code: string;
  status: FinishedGoodsProductionStatus;
  qty_produced: number;
  created_at: string;
  completed_at?: string | null;
  cancelled_at?: string | null;
}

export interface FinishedGoodsProductionIngredientDraftRow {
  bean_id: string;
  roast_type: string;
  percentage: number;
  gram: number;
}

export interface FinishedGoodsProductionDetail {
  id: string;
  code?: string;
  fg_id: string;
  status: FinishedGoodsProductionStatus;
  qty_produced: number;
  created_at: string;
  completed_at?: string | null;
  cancelled_at?: string | null;
  finished_good?: {
    id: string;
    code?: string;
    name?: string;
    pack_size?: number | null;
    location?: string | null;
    estimated_selling_price?: number | null;
  };
  ingredients: FinishedGoodsProductionIngredientDraftRow[];
  misc_costs: Array<{ name: string; unit_price: number; unit: number; total_price?: number }>;
  // Actual booked COGS for a completed production (nil for drafts). When present,
  // the modal shows these instead of recomputing, so preview == inventory.
  total_cogs?: number | null;
  unit_cost_per_piece?: number | null;
}

// FinishedGoodsCostEstimateRequest/Response — read-only COGS preview computed by
// the backend with the SAME FIFO pricing as a real production, so the modal never
// drifts from the stored value.
export interface FinishedGoodsCostEstimateRequest {
  final_quantity_pcs: number;
  components: {
    item_id: string;
    roast_type: string;
    quantity_kg: number;
  }[];
  misc_costs?: {
    name: string;
    unit_price: number;
    unit: number;
  }[];
}

export interface FinishedGoodsCostEstimateResponse {
  total_input_cost: number;
  total_misc_cost: number;
  total_cogs: number;
  unit_cost_per_piece: number;
}

export interface CreateFinishedGoodsProductionDraftRequest {
  fg_id: string;
  qty_produced: number;
  finished_good?: {
    location?: string;
    estimated_selling_price?: number;
    pack_size?: number;
  };
  ingredients?: FinishedGoodsProductionIngredientDraftRow[];
  misc_costs?: {
    name: string;
    unit_price: number;
    unit: number;
  }[];
}

export interface CreateFinishedGoodsProductionDraftResponse {
  production_id: string;
  fg_id: string;
  status: FinishedGoodsProductionStatus;
  code?: string;
}

export interface CreateFinishedGoodsProductionDraftNewRequest {
  finished_good: {
    name: string;
    category?: string;
    location?: string;
    estimated_selling_price?: number;
    pack_size?: number;
  };
  qty_produced: number;
  is_onboarding_sample?: boolean;
  ingredients?: FinishedGoodsProductionIngredientDraftRow[];
  misc_costs?: {
    name: string;
    unit_price: number;
    unit: number;
  }[];
}

export interface CompleteFinishedGoodsProductionDraftRequest {
  qty_produced: number;
  ingredients: FinishedGoodsProductionIngredientDraftRow[];
  misc_costs?: {
    name: string;
    unit_price: number;
    unit: number;
  }[];
}

// Finished good ingredient configuration (as returned by backend)
export interface FinishedGoodIngredient {
  inventory_item_id: string;
  roast_type: string;
  grams_per_pack: number; // grams per piece/pack
  percentage?: number;
  item_code?: string;
  item_name?: string;
  updated_at?: string;
}

export const mapDtoToInventoryItem = (dto: InventoryItemDto): InventoryItem => {
  const quantityAvailable = typeof dto.quantity_available === 'number' && Number.isFinite(dto.quantity_available)
    ? dto.quantity_available
    : 0;

  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    description: dto.description,
    unitOfMeasure: dto.unit_of_measure,
    itemType: dto.item_type,
    reorderPoint: dto.reorder_point,
    reorderQuantity: dto.reorder_quantity,
    standardCost: dto.standard_cost,
    stock: quantityAvailable,
    quantity: quantityAvailable,
    status: dto.status as 'active' | 'inactive',
    expiryDate: dto.expiry_date,
    workOrderId: dto.work_order_id,
    woReferenceId: dto.wo_reference_id,
    greenBeanProfileId: dto.green_bean_profile_id,
    location: dto.location,
    greenBeanProfiles: dto.green_bean_profiles,
    roastProfiles: dto.roast_profiles,
    cuppingProfiles: dto.cupping_profiles,
    averagePrice: dto.average_price,
    estimatedSellingPrice: dto.estimated_selling_price,
    packSizeGrams: dto.pack_size,
    ingredients: dto.ingredients ? dto.ingredients.map(mapDtoToInventoryItem) : undefined,
    isOnboardingSample: dto.is_onboarding_sample ?? false,
  };
};

export const mapDtoToStockMovement = (dto: StockMovementDto): StockMovement => {
  return {
    id: dto.id,
    itemId: dto.item_id,
    warehouseId: dto.warehouse_id,
    movementType: dto.movement_type as 'receipt' | 'issue' | 'transfer' | 'adjustment',
    quantity: dto.quantity,
    referenceType: dto.reference_type,
    referenceId: dto.reference_id,
    notes: dto.notes,
    createdAt: dto.created_at,
    warehouseName: dto.warehouse_name,
    userName: dto.user_name,
    unitCost: dto.unit_cost,
    lotId: dto.lot_id,
    lotNumber: dto.lot_number,
  };
};

export const mapFormDataToDto = (formData: InventoryItemFormData): CreateInventoryItemRequest => {
  return {
    code: formData.code?.trim() || undefined,
    name: formData.name,
    description: formData.description || '',
    unit_of_measure: formData.unitOfMeasure,
    item_type: formData.itemType,
    reorder_point: formData.reorderPoint,
    reorder_quantity: formData.reorderQuantity ?? 0,
    standard_cost: formData.standardCost || 0,
    estimated_selling_price: formData.estimatedSellingPrice,
    expiry_date: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : undefined,
    quantity: formData.quantity,
    location: formData.location,
    work_order_id: formData.workOrderId,
    status: formData.status,
    green_bean_profile_id: formData.greenBeanProfileId || undefined,
    new_green_bean_profile: formData.green_bean_profile,
    pack_size: formData.packSizeGrams,
  };
};

export const mapDtoToInventoryLot = (dto: InventoryLotDto): InventoryLot => {
  return {
    id: dto.id,
    inventoryItemId: dto.item_id,
    lotNumber: dto.lot_number,
    initialQuantity: dto.initial_quantity,
    remainingQuantity: dto.remaining_quantity,
    expiryDate: dto.expiry_date,
    receivedDate: dto.received_date,
    supplierId: dto.supplier_id,
    supplierName: dto.supplier_name,
    supplierEmail: dto.supplier_email,
    supplierPhone: dto.supplier_phone,
    sourceType: dto.source_type,
    sourceId: dto.source_id,
    sourceRef: dto.source_ref,
    itemName: dto.item_name,
    itemCode: dto.item_code,
    unitPrice: dto.unit_price,
    isSample: dto.is_sample,
    photoName: dto.photo_name,
    photoUrl: dto.photo_url,
    photoUploadedAt: dto.photo_uploaded_at,
  };
};

export const mapDtoToInventorySample = (dto: InventorySampleDto): InventorySample => {
  return {
    id: dto.id,
    inventoryItemId: dto.inventoryItemId,
    sampleNumber: dto.sampleNumber,
    date: dto.receivedDate,
    notes: dto.notes,
    createdAt: dto.createdAt,
    greenBeanProfileId: dto.greenBeanProfile?.id,
    roastProfileId: dto.roastProfile?.id,
    cuppingProfileId: dto.cuppingProfile?.id,
    qaPassed: dto.qaPassed,
    lotId: dto.lotId,
    lotNumber: dto.lotNumber,
  };
};

export const mapDtoToRoastedBulkGroup = (dto: RoastedBulkGroupDto): RoastedBulkGroup => {
  return {
    itemId: dto.itemId,
    itemCode: dto.itemCode,
    itemName: dto.itemName,
    roastType: dto.roastType,
    totalStock: dto.totalStock,
    averagePrice: dto.averagePrice,
    items: dto.items ? dto.items.map(mapDtoToRoastedBulkGroupItem) : undefined,
  };
};

export const mapDtoToRoastedBulkGroupItem = (dto: RoastedBulkGroupItemDto): RoastedBulkGroupItem => {
  return {
    workOrderId: dto.workOrderId,
    workOrderNumber: dto.workOrderNumber,
    inputWeight: dto.inputWeight,
    outputWeight: dto.outputWeight,
    shrinkageKg: dto.shrinkageKg,
    shrinkagePercent: dto.shrinkagePercent ?? null,
    remainingQuantity: dto.remainingQuantity,
    expiryDate: dto.expiryDate,
    status: dto.status,
    inventoryItemId: dto.inventoryItemId,
    lotId: dto.lotId,
    unitPrice: dto.unitPrice,
    unitCostPerKg: dto.unitCostPerKg,
    stockCreatedAt: dto.stockCreatedAt,
  };
};
